import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { ErrorState } from '@bb/ui';
import { ApiError, type AdminApi, type PushAnalyticsFilters } from '../../../api/client.ts';
import { PeriodPicker } from '../PeriodPicker.tsx';
import { lastDays, periodProblem, type Period } from '../period.ts';
import { PushPerformanceTable } from './PushPerformanceTable.tsx';
import { SuppressedReasonsChart } from './SuppressedReasonsChart.tsx';

type GroupBy = NonNullable<PushAnalyticsFilters['groupBy']>;
type Basis = NonNullable<PushAnalyticsFilters['basis']>;
const GROUPS: Array<[GroupBy, string]> = [
  ['platform', 'Platform'],
  ['day', 'Day'],
  ['variant', 'Variant'],
];
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

export interface PushPerformancePanelProps {
  environmentId: string;
  /** A campaign, a Canvas or one Message step of a Canvas (`canvasStepId` = its `stepUuid`); none — the whole environment. */
  scope?: { campaignId?: string; canvasId?: string; canvasStepId?: string };
  /** The page's period; without it the panel has its own picker (last 30 days). */
  period?: Period;
  /** «Send cohort / event day» switch: only where days are compared across messages (environment page). */
  allowBasis?: boolean;
  variantLabels?: Record<string, string>;
  /** Heading level of the panel title inside the page. */
  heading?: 'h2' | 'h3';
  /** More options next to «Group by» (the Canvas page's step choice). */
  toolbarExtra?: ReactNode;
  /** A line under the options, e.g. which step the numbers are for. */
  note?: ReactNode;
}

/**
 * Push performance of an environment, a campaign or a Canvas (BB-79): the per-delivery table with each metric's
 * definition, and why pushes were not sent. Mounted on the environment analytics page, the campaign page (until the
 * campaign analytics of BB-104) and the Canvas analytics page (BB-123), where it can narrow to one Message step.
 */
export function PushPerformancePanel({ environmentId, scope = {}, period: pagePeriod, allowBasis = false, variantLabels, heading = 'h2', toolbarExtra, note }: PushPerformancePanelProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [ownPeriod, setOwnPeriod] = useState<Period>(() => lastDays(30));
  const [groupBy, setGroupBy] = useState<GroupBy>('platform');
  const [basis, setBasis] = useState<Basis>('send_cohort');
  const period = pagePeriod ?? ownPeriod;
  const valid = periodProblem(period) === null;
  const filters: PushAnalyticsFilters = { ...scope, from: period.from, to: period.to, groupBy, basis };
  const performance = useQuery({ queryKey: ['push-analytics', environmentId, filters], queryFn: () => api.pushAnalytics(environmentId, filters), enabled: valid, placeholderData: keepPreviousData });
  const { groupBy: _g, basis: _b, ...reasonsFilters } = filters;
  const reasons = useQuery({ queryKey: ['push-analytics', environmentId, 'suppressed', reasonsFilters], queryFn: () => api.pushSuppressedReasons(environmentId, reasonsFilters), enabled: valid, placeholderData: keepPreviousData });
  const Heading = heading;
  const Sub = heading === 'h2' ? 'h3' : 'h4';
  const data = performance.data;
  return (
    <section className="panel push-performance" aria-label="Push performance">
      <Heading>Push performance</Heading>
      {!pagePeriod && <PeriodPicker value={ownPeriod} onChange={setOwnPeriod} />}
      <div className="toolbar" role="group" aria-label="Push performance options">
        <label>
          Group by{' '}
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
            {GROUPS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {allowBasis && (
          <label>
            Count by{' '}
            <select value={basis} onChange={(e) => setBasis(e.target.value as Basis)}>
              <option value="send_cohort">Send day (cohort)</option>
              <option value="event_day">Event day</option>
            </select>
          </label>
        )}
        {toolbarExtra}
      </div>
      {note}
      {performance.isError ? (
        <ErrorState title="Push performance could not be loaded" message={errorText(performance.error)} onRetry={() => void performance.refetch()} />
      ) : !data ? (
        valid && <p aria-busy="true">Loading push performance…</p>
      ) : (
        <>
          <p className="hint">
            {data.basis === 'send_cohort' ? 'Counted per push on the day it was sent: opens and receipts count towards the day of their push.' : 'Counted on the day each event happened, in the environment time zone.'} Test sends and control groups are left out. Influenced opens: first app session within {data.influencedOpenWindowMinutes} min of a push, without a tap.
          </p>
          {data.rows.length === 0 ? <p className="hint">No pushes were sent in this period.</p> : <PushPerformanceTable data={data} {...(variantLabels ? { variantLabels } : {})} />}
        </>
      )}
      <Sub>Not sent and failed</Sub>
      {reasons.isError ? (
        <ErrorState title="Reasons could not be loaded" message={errorText(reasons.error)} onRetry={() => void reasons.refetch()} />
      ) : reasons.data ? (
        <SuppressedReasonsChart data={reasons.data} />
      ) : (
        valid && <p aria-busy="true">Loading reasons…</p>
      )}
    </section>
  );
}
